
import React, { useState, useMemo, useEffect } from 'react';
import { Box, Text, VStack, HStack, Collapse, Input, Spacer, Spinner, Center } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';

const HierarchicalSelectItem = ({ item, onSelect, level, startOpen = false }) => {
  const [isOpen, setIsOpen] = useState(startOpen);

  useEffect(() => {
    setIsOpen(startOpen);
  }, [startOpen]);

  const hasChildren = (item.hospitals && item.hospitals.length > 0) ||
    (item.departments && item.departments.length > 0) ||
    (item.providers && item.providers.length > 0) ||
    (item.spocs && item.spocs.length > 0);

  const handleToggle = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleSelect = (e, selectableItem, selectedItemType, parentType) => {
    e.stopPropagation();

    if (!selectableItem || typeof selectableItem.id === 'undefined') {
      return;
    }

    let selected = {
      // value: selectableItem.id,
      value: (selectableItem.spocs && selectableItem.spocs.length > 0 && selectableItem.spocs[0].user) ? selectableItem.spocs[0].user.id : selectableItem.id,
      label: ''
    }
    if (selectedItemType === 'spoc') {
      selected.label = `${selectableItem.first_name || ''} ${selectableItem.last_name || ''}`.trim();
      selected.clientType = itemTypeObject;
    } else { // provider
      selected.label = selectableItem.name;
      selected.clientType = {provider_id: selectableItem.id}
    }

    // Construct the full descriptive label for the message
    selected.fullLabel = selected.label;
    if (selectedItemType === 'spoc') {
      if (parentType) {
        selected.fullLabel += ` (${parentType} SPOC)`;
      } else {
        selected.fullLabel += ` (SPOC)`;
      }
    } else { // provider
      if (parentType) {
        selected.fullLabel += ` (${parentType} Provider)`;
      } else {
        selected.fullLabel += ` (Provider)`;
      }
    }

    onSelect(selected);
  };

  const itemType = item.hospitals ? 'Health System' : item.departments ? 'Hospital' : item.providers ? 'Department' : null;
  const itemTypeObject = useMemo(() => {
    if (!itemType) {
      return;
    }
    let key = null;
    if (itemType === 'Health System') {
      key = 'health_system_id';
    } else if (itemType === 'Hospital') {
      key = 'hospital_id';
    } else if (itemType === 'Department') {
      key = 'department_id';
    }
    if (!key) {
      return null;
    }

    return {[key]: item.id};
  }, [itemType])

  return (
    <VStack align="start" spacing={1} pl={level > 0 ? 4 : 0} w="100%">
      <HStack onClick={handleToggle} cursor="pointer" w="100%" _hover={{ bg: "gray.100" }} p={1} borderRadius="md" alignItems="baseline">
        {hasChildren ? (
          isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />
        ) : (
          <Box w="14px" /> // placeholder for alignment
        )}
        <Text fontWeight="bold">{item.name}</Text>{itemType && <Text ml={2} fontSize="xs" color="gray.500">{itemType}</Text>}
      </HStack>

      <Collapse in={isOpen} style={{ width: '100%' }}>
        <VStack align="start" spacing={1} pl={4}>
          {item.spocs && item.spocs.map(spoc => (
            (spoc && spoc.user && typeof spoc.user.id !== 'undefined') ? (
              <HStack key={spoc.user.id} onClick={(e) => handleSelect(e, spoc.user, 'spoc', itemType)} cursor="pointer" _hover={{ bg: "gray.100" }} p={1} borderRadius="md" w="100%" alignItems="baseline">
                <Text>{`${spoc.user.first_name || ''} ${spoc.user.last_name || ''}`.trim()}</Text>
                <Spacer />
                <Text fontSize="xs" color="gray.500">SPOC</Text>
              </HStack>
            ) : null
          ))}

          {item.hospitals && item.hospitals.map(hospital => (
            <HierarchicalSelectItem key={hospital.id} item={hospital} onSelect={onSelect} level={level + 1} startOpen={startOpen} />
          ))}

          {item.departments && item.departments.map(department => (
            <HierarchicalSelectItem key={department.id} item={department} onSelect={onSelect} level={level + 1} startOpen={startOpen} />
          ))}

          {item.providers && item.providers.map(provider => (
            <HStack key={provider.id} onClick={(e) => handleSelect(e, provider, 'provider', itemType)} cursor="pointer" _hover={{ bg: "gray.100" }} p={1} borderRadius="md" w="100%" alignItems="baseline">
              <Text>{provider.name}</Text>
              <Spacer />
              <Text fontSize="xs" color="gray.500">Provider</Text>
            </HStack>
          ))}
        </VStack>
      </Collapse>
    </VStack>
  );
};


const HierarchicalSelect = ({ data, onSelect, placeholder, errorText, isLoading = false }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSelect = (item) => {
    setSelectedItem(item);
    onSelect(item);
    setIsOpen(false);
    setSearchTerm('');
  };

  const filterData = (items, term) => {
    const lowerTerm = term.toLowerCase();
    if (!lowerTerm) return items;

    const filterItems = (items) => {
      return items.reduce((acc, item) => {
        if (item.name.toLowerCase().includes(lowerTerm)) {
          acc.push(item);
          return acc;
        }

        const matchingSpocs = item.spocs?.filter(spoc =>
          spoc.user && `${spoc.user.first_name || ''} ${spoc.user.last_name || ''}`.trim().toLowerCase().includes(lowerTerm)
        );
        const matchingProviders = item.providers?.filter(provider =>
          provider.name.toLowerCase().includes(lowerTerm)
        );

        const matchingHospitals = item.hospitals ? filterItems(item.hospitals) : [];
        const matchingDepartments = item.departments ? filterItems(item.departments) : [];

        if (matchingSpocs?.length > 0 || matchingProviders?.length > 0 || matchingHospitals.length > 0 || matchingDepartments.length > 0) {
          acc.push({
            ...item,
            hospitals: matchingHospitals,
            departments: matchingDepartments,
            spocs: matchingSpocs,
            providers: matchingProviders,
          });
        }

        return acc;
      }, []);
    };

    return filterItems(items);
  };

  const filteredData = useMemo(() => filterData(data, searchTerm), [data, searchTerm]);

  return (
    <Box w="100%">
      <Box
        borderWidth="1px"
        borderRadius="md"
        p={2}
        onClick={() => !isLoading && setIsOpen(!isOpen)}
        cursor={isLoading ? 'not-allowed' : 'pointer'}
        borderColor={errorText ? 'red.500' : 'inherit'}
      >
        <HStack>
          <Text flex="1">{selectedItem ? selectedItem.label : placeholder}</Text>
          {isLoading ? <Spinner size="sm" /> : (isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />)}
        </HStack>
      </Box>
      {errorText && <Text color="red.500" fontSize="sm">{errorText}</Text>}
      <Collapse in={isOpen}>
        <Box borderWidth="1px" borderRadius="md" mt={1} p={2}>
          <Input
            placeholder="Search..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            mb={2}
          />
          <Box maxH="250px" overflowY="auto"> {/* Inner scrollable box */}
            {isLoading ? (
              <Center p={4}>
                <Spinner />
              </Center>
            ) : filteredData.length > 0 ? (
              filteredData.map(item => (
                <HierarchicalSelectItem key={item.id} item={item} onSelect={handleSelect} level={0} startOpen={!!searchTerm} />
              ))
            ) : (
              <Center p={4}>
                <Text>No data</Text>
              </Center>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default HierarchicalSelect;
